// utils/invoiceTransformers.ts
import { WorkingHour, InvoiceCalculationsPayload } from '../features/invoice/invoice.service';

/**
 * Constants from InvoiceTool.js
 */
const AMOUNT_PER_UNIT = {
  MILEAGE: 2.5, // SEK per km
};

const NEW_ARTICLES = {
  MILEAGE: 'mileage',
  ALLOWANCE_OUT: 'allowance_out',
  ALLOWANCE_IN: 'allowance_in',
  TRAVEL: 'travel',
  MATERIAL_VAT: 'material_vat',
};

const HourlyWages = '92'; // Article number for hourly wages

/**
 * Calculate individual article amount
 */
const getArticleIndividualAmount = (
  articleKey: string,
  flagKey: string,
  userOtherArticles: any[]
): number => {
  return (userOtherArticles || [])
    .filter((item) => item.key === articleKey && item.data?.[flagKey] === "1")
    .reduce((sum, item) => sum + (Number(item?.value) || 0), 0);
};

/**
 * Calculate difference in dates
 */
const diffInDays = (
  key: string,
  tripEndedDateIs: string,
  tripStartedDateIs: string,
  tripEndedDateOs: string,
  tripStartedDateOs: string
): number => {
  let startDate: Date, endDate: Date;
  
  if (key === NEW_ARTICLES.ALLOWANCE_IN) {
    startDate = new Date(tripStartedDateIs);
    endDate = new Date(tripEndedDateIs);
  } else {
    startDate = new Date(tripStartedDateOs);
    endDate = new Date(tripEndedDateOs);
  }
  
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays || 1;
};

/**
 * Get article number for other articles
 */
const getArticleNumber = (item: any): string => {
  const articleMap: Record<string, string> = {
    [NEW_ARTICLES.MILEAGE]: '95',
    [NEW_ARTICLES.ALLOWANCE_OUT]: '96',
    [NEW_ARTICLES.ALLOWANCE_IN]: '96',
    [NEW_ARTICLES.TRAVEL]: '97',
    [NEW_ARTICLES.MATERIAL_VAT]: '98',
  };
  return articleMap[item.key] || '99';
};

/**
 * Get unit for other articles
 */
const getOtherArticlesUnit = (item: any, field: string): string => {
  if (item.key === NEW_ARTICLES.MILEAGE) return 'Km';
  if (item.key === NEW_ARTICLES.ALLOWANCE_OUT || item.key === NEW_ARTICLES.ALLOWANCE_IN) return 'Dagar';
  return item?.data?.[field] || '';
};

/**
 * Check if article is tax free
 */
const isArticleTaxFree = (item: any): boolean => {
  return item.key === NEW_ARTICLES.MATERIAL_VAT && item?.data?.vat === 0;
};

/**
 * Transform dates and invoiceHours from InvoiceTool.js format to API format
 * This matches the logic from calculateInvoiceAmounts.js
 */
export const transformWorkingHoursForUser = (
  userInvoiceHours: Record<string, any>,
  userOtherArticles: any[],
  vat: number,
  currencyGlobalValue: number,
  allowancePerDaySweden: { allowance_in: number; allowance_out: number },
  anyArrayHasValue: boolean
): WorkingHour[] => {
  const totalWorkingDates: WorkingHour[] = [];

  // Add regular working hours
  Object.keys(userInvoiceHours || {}).forEach((date) => {
    const [year, month, day] = date.split("-");
    const item = userInvoiceHours[date];

    totalWorkingDates.push({
      id: 0,
      workingDate: `${year}-${month}-${day}`,
      dateId: date,
      number: item.units,
      unit: item.article.unit,
      vat: vat,
      isArticleShownInInvoice: true,
      amountPerUnit: item.amount,
      amount: item.amount * item.units,
      articleNumber: item.article?.articleNumber,
      workingDescription: item.workingDescription || "",
      houseworkType: item.houseworkType ? item.houseworkType.value : "",
      workingHour: item.article?.articleNumber === HourlyWages ? item.units : 0,
    });
  });

  // Add other articles (mileage, allowance, travel, materials)
  if (anyArrayHasValue && userOtherArticles) {
    (userOtherArticles || []).forEach((item) => {
      const requiredFlags = [
        "isMileageArticleShowInInvoice",
        "isArticleShowInInvoiceOS",
        "isArticleShowInInvoiceIS",
        "isTravelArticleShowInInvoice",
        "isMaterialArticleShowInInvoice",
      ];

      totalWorkingDates.push({
        id: item?.data?.id || 0,
        workingDate: item?.data?.tripStartedDate?.split("T")[0] || "",
        dateId: item?.data?.dateId || `${Date.now()}-${Math.random()}`,
        number:
          item.key === NEW_ARTICLES.MILEAGE
            ? item?.data?.distance
            : item.key === NEW_ARTICLES.ALLOWANCE_OUT || item.key === NEW_ARTICLES.ALLOWANCE_IN
            ? diffInDays(
                item?.key,
                item?.data?.tripEndedDateIs || item?.data?.tripEndedDate,
                item?.data?.tripStartedDateIs || item?.data?.tripStartedDate,
                item?.data?.tripEndedDateOs || item?.data?.tripEndedDate,
                item?.data?.tripStartedDateOs || item?.data?.tripStartedDate
              )
            : 1,
        unit: getOtherArticlesUnit(item, "unit"),
        amountPerUnitSek:
          item.key === NEW_ARTICLES.MILEAGE
            ? AMOUNT_PER_UNIT.MILEAGE
            : item.key === NEW_ARTICLES.ALLOWANCE_OUT || item.key === NEW_ARTICLES.ALLOWANCE_IN
            ? item?.data?.allowancePerDay || item?.data?.amountPerUnitSek || allowancePerDaySweden[item?.key as keyof typeof allowancePerDaySweden]
            : Number(item?.value),
        amountPerUnit:
          item.key === NEW_ARTICLES.MILEAGE
            ? AMOUNT_PER_UNIT.MILEAGE / (isNaN(parseFloat(currencyGlobalValue as any)) ? 1 : Number(currencyGlobalValue))
            : item.key === NEW_ARTICLES.ALLOWANCE_OUT || item.key === NEW_ARTICLES.ALLOWANCE_IN
            ? (isNaN(parseFloat(item?.data?.allowancePerDay || item?.data?.amountPerUnitSek || allowancePerDaySweden[item?.key as keyof typeof allowancePerDaySweden])) ? 0 : Number(item?.data?.allowancePerDay || item?.data?.amountPerUnitSek || allowancePerDaySweden[item?.key as keyof typeof allowancePerDaySweden])) /
              (isNaN(parseFloat(currencyGlobalValue as any)) ? 1 : Number(currencyGlobalValue))
            : (isNaN(parseFloat(item?.value)) ? 0 : Number(item?.value)) /
              (isNaN(parseFloat(currencyGlobalValue as any)) ? 1 : Number(currencyGlobalValue)),
        articleNumber: getArticleNumber(item),
        workingDescription: item?.label || "",
        houseworkType: "",
        workingHour: 0,
        isTaxFreeArticle: isArticleTaxFree(item),
        isArticleShownInInvoice:
          requiredFlags.every((flag) => item.data?.[flag] === "1") || item?.data?.isArticleShownInInvoice,
        isPdfAttachedToInvoice: true,
        tripStartedPlace: item?.data?.tripStartedPlace,
        tripEndedPlace: "",
        distance: item?.data?.distance || 0,
        country:
          item?.key === NEW_ARTICLES.ALLOWANCE_OUT
            ? item?.data?.country
            : item?.key === NEW_ARTICLES.ALLOWANCE_IN
            ? "Sweden"
            : "",
        allowancePerDay: 0,
        reasonForTrip: item?.data?.reasonForTrip || "",
        carRegistrationNo: item?.data?.carRegistrationNo || "",
        vat: item?.data?.vat != null ? Number(item?.data?.vat) : vat,
        tripStartedDate:
          item?.data?.tripStartedDate?.split("T")[0] ||
          (item?.key === NEW_ARTICLES.ALLOWANCE_OUT ? item?.data?.tripStartedDateOs : item?.data?.tripStartedDateIs),
        tripEndedDate:
          item?.data?.tripEndedDate?.split("T")[0] ||
          (item?.key === NEW_ARTICLES.ALLOWANCE_OUT ? item?.data?.tripEndedDateOs : item?.data?.tripEndedDateIs),
        amount: isNaN(parseFloat(item?.valueInSelectedCurrency)) ? 0.0 : Number(item?.valueInSelectedCurrency),
        totalDayOfTravels: diffInDays(
          item?.key,
          item?.data?.tripEndedDateIs || item?.data?.tripEndedDate,
          item?.data?.tripStartedDateIs || item?.data?.tripStartedDate,
          item?.data?.tripEndedDateOs || item?.data?.tripEndedDate,
          item?.data?.tripStartedDateOs || item?.data?.tripStartedDate
        ),
        allowanceDestination: item?.data?.destination || item?.data?.allowanceDestination || "",
        expenseCategory: "",
        fileName: "",
        fileContent: "",
        amountInSek: Number(item?.value),
        isHalfDay: item?.data?.isHalfDay,
        foodAllowance: item?.data?.totalFoodRate || 0,
        articleId: item?.data?.id || 0,
      });
    });
  }

  return totalWorkingDates;
};

/**
 * Build invoice calculation payload for a single user
 * This matches the logic from calculateInvoiceAmounts.js
 */
export const buildInvoiceCalculationPayloadForUser = (
  userInvoiceHours: Record<string, any>,
  userOtherArticles: any[],
  userIndex: number,
  params: {
    vat: number;
    currency: string;
    currencyGlobalValue: number;
    globalData: any;
    pension?: boolean | number | Record<number, boolean | number>;
    pensionAmount?: number | Record<number, number>;
    holidaySavings?: boolean | number | Record<number, boolean | number>;
    holidaySavingFee?: number | Record<number, number>;
    rutDeduction: boolean;
    rootDeduction: boolean;
    expressPayout: boolean;
    expenditureSum: number;
    allowancePerDaySweden: { allowance_in: number; allowance_out: number };
    anyArrayHasValue: boolean;
    selectedCustomer: any;
    customer: any;
    isPreliminaryInvoice?: boolean;
    prefillingData?: any;
  }
): InvoiceCalculationsPayload => {
  const {
    vat,
    currency,
    currencyGlobalValue,
    globalData,
    pension,
    pensionAmount,
    holidaySavings,
    holidaySavingFee,
    rutDeduction,
    rootDeduction,
    expressPayout,
    expenditureSum,
    allowancePerDaySweden,
    anyArrayHasValue,
    selectedCustomer,
    customer,
    isPreliminaryInvoice = false,
    prefillingData,
  } = params;

  // Calculate total amount from working hours
  const totalAmount = Object.values(userInvoiceHours || {}).reduce(
    (total: number, entry: any) => total + entry.units * entry.amount,
    0
  );

  // Calculate hourly wages amount (article 92)
  const userArticleHourlyAmount = Object.values(userInvoiceHours || {}).reduce(
    (total: number, entry: any) =>
      entry.article.articleNumber === HourlyWages ? total + entry.units * entry.amount : total,
    0
  );

  // Calculate other article amounts
  const mileageAmount = getArticleIndividualAmount("mileage", "isMileageArticleShowInInvoice", userOtherArticles);
  const allowanceOsAmount = getArticleIndividualAmount("allowance_out", "isArticleShowInInvoiceOS", userOtherArticles);
  const allowanceIsAmount = getArticleIndividualAmount("allowance_in", "isArticleShowInInvoiceIS", userOtherArticles);
  const travelAmount = getArticleIndividualAmount("travel", "isTravelArticleShowInInvoice", userOtherArticles);
  const expAmount = getArticleIndividualAmount("material_vat", "isMaterialArticleShowInInvoice", userOtherArticles);

  const userTotalAmount =
    totalAmount + mileageAmount + allowanceOsAmount + allowanceIsAmount + travelAmount + expAmount;

  // Build working hours array
  const workingHours = transformWorkingHoursForUser(
    userInvoiceHours,
    userOtherArticles,
    vat,
    currencyGlobalValue,
    allowancePerDaySweden,
    anyArrayHasValue
  );

  // Handle pension values - support both single value and per-user object
  const getPensionValue = (value: any, index: number) => {
    if (value && typeof value === "object") {
      const pensionVal = value[index];
      return pensionVal !== undefined && pensionVal !== null && pensionVal !== "" && pensionVal > 0
        ? pensionVal
        : prefillingData?.getInvoiceData?.freelancer?.[index]?.pension;
    }
    return value;
  };

  const currentPension = getPensionValue(pension, userIndex);
  const currentPensionAmount = getPensionValue(pensionAmount, userIndex);
  const currentHolidaySavings = getPensionValue(holidaySavings, userIndex);
  const currentHolidaySavingFee = getPensionValue(holidaySavingFee, userIndex);

  // Get currency rate
  const currencyRate = globalData?.currencies?.find((item: any) => item.description === currency)?.globalValue || currencyGlobalValue;

  // Build payload
  return {
    vat: vat,
    travel: 0,
    allowance: 0,
    isPreliminaryInvoice: isPreliminaryInvoice,
    pension: currentPension,
    amount: parseFloat(userTotalAmount.toString()),
    rutDeduction: rutDeduction,
    rootDeduction: rootDeduction,
    expressPayout: expressPayout,
    holidaySavings: currentHolidaySavings,
    pensionFee: 0,
    pensionAmount: currentPensionAmount || 0,
    tax: parseFloat(globalData?.tax?.globalValue || 30),
    fee: parseFloat(globalData?.fee?.globalValue || 1.9),
    expenditureForMaterialsAndToolsTaxFreeAmount: expenditureSum || 0,
    HourlyWagesAmount: userArticleHourlyAmount || 0,
    rutTaxValue: parseFloat(globalData?.rutTax?.globalValue || 50),
    socialFee: parseFloat(globalData?.socialFee?.globalValue || 23.91),
    rootTaxValue: parseFloat(globalData?.rootTax?.globalValue || 50),
    expressFee: expressPayout ? Number(globalData?.expressFee?.globalValue || 0) : 0,
    holidaySavingsFee: currentHolidaySavingFee || 0,
    currencyRate: currencyRate?.toString() || "1",
    currencyName: currency,
    invoiceType: customer?.customerAddress?.[0]?.invoiceType ?? customer?.invoiceType ?? undefined,
    numberOfFreelancers: 1, // Will be updated when calling for multiple users
    workingHours: workingHours,
    customerId: selectedCustomer?.customerId || 0,
    createdDate: new URLSearchParams(window.location.search).get("createdDate") || new Date().toISOString(),
    email: selectedCustomer?.email || customer?.email || "", // Add customer email
  };
};

/**
 * Calculate total from separated user calculations
 */
export const getSeparatedUserCalculationsTotal = (calculations: any[]): any => {
  if (!calculations || calculations.length === 0) return null;
  
  return calculations.reduce((total, calc) => {
    if (!total) return calc;
    
    return {
      net: (total.net || 0) + (calc.net || 0),
      tax: (total.tax || 0) + (calc.tax || 0),
      amount: (total.amount || 0) + (calc.amount || 0),
      pensionPercentage: (total.pensionPercentage || 0) + (calc.pensionPercentage || 0),
      pension: (total.pension || 0) + (calc.pension || 0),
      holidayPayAmount: (total.holidayPayAmount || 0) + (calc.holidayPayAmount || 0),
      holidayPayAmountInSek: (total.holidayPayAmountInSek || 0) + (calc.holidayPayAmountInSek || 0),
      expressFee: (total.expressFee || 0) + (calc.expressFee || 0),
      expressFeeInSek: (total.expressFeeInSek || 0) + (calc.expressFeeInSek || 0),
      rutDeductionAmount: (total.rutDeductionAmount || 0) + (calc.rutDeductionAmount || 0),
      rootDeductionAmount: (total.rootDeductionAmount || 0) + (calc.rootDeductionAmount || 0),
      taxInSek: (total.taxInSek || 0) + (calc.taxInSek || 0),
      netInSek: (total.netInSek || 0) + (calc.netInSek || 0),
    };
  }, null);
};
